import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import { formatCount } from "@shared-core/domain/formatters";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";

// One parcel or pallet run as the customer follows it (order to invoice doc
// 102 §9.5): who carries it, the tracking reference, and the carrier's own
// tracking page. The order's Delivery tab shows one per consignment to each
// address, each with the server's sentence for when it arrives (customer
// delivery updates) above how it was sent. A carrier we cannot name has no
// carrier line or link, only its sentence and the number it was sent under.

const DAY_TONE = {
  normal: "",
  good: "text-green-700 dark:text-green-400",
  attention: "text-amber-700 dark:text-amber-400",
} as const;

export function ParcelTracking({
  carrier,
  boxes = null,
  trackingReference,
  trackingUrl,
  isLinkProminent = false,
  title = null,
  day = null,
  dayTone = "normal",
  children,
}: {
  carrier: string | null | undefined;
  boxes?: number | null;
  trackingReference: string | null | undefined;
  trackingUrl: string | null | undefined;
  /** The carrier's page first, when a courier problem sends the customer there. */
  isLinkProminent?: boolean;
  /** "Parcel 1 of 2", where several went to one address. */
  title?: string | null;
  /** The server's sentence for when it arrives or did ("Arriving Thursday 30 October"). */
  day?: string | null;
  dayTone?: keyof typeof DAY_TONE;
  /** Under the link: the parcel's timeline, where the page has one. */
  children?: ReactNode;
}) {
  const parcel = [
    carrier ? `Sent by ${carrier}` : null,
    boxes ? `${formatCount(boxes)} ${boxes === 1 ? "box" : "boxes"}` : null,
  ].filter(Boolean);
  if (parcel.length === 0 && !trackingReference && !day) return null;
  return (
    <div className="space-y-3 rounded-lg bg-muted/50 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          {title && (
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
          )}
          {day && <p className={cn("font-medium", DAY_TONE[dayTone])}>{day}</p>}
          {parcel.length > 0 && <p>{parcel.join(" · ")}</p>}
          {trackingReference && (
            <p className="text-muted-foreground">
              Tracking{" "}
              <span className="font-mono text-foreground">
                {trackingReference}
              </span>
            </p>
          )}
        </div>
        {trackingUrl && (
          <Button
            asChild
            size="sm"
            variant={isLinkProminent ? "default" : "outline"}
          >
            <a href={trackingUrl} target="_blank" rel="noreferrer">
              {carrier ? `Track with ${carrier}` : "Track your parcel"}
              <ExternalLink className="size-3.5" />
            </a>
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}
